import { Segmented } from '@/components/ui'
import type { ExecutionMode } from '@/lib/types'

/**
 * Sélecteur de mode d'exécution global (§7).
 *
 * Visible en permanence et persistant : c'est le réglage qui décide si un
 * appui envoie un ordre réel ou ne fait qu'afficher un calcul. Le masquer dans
 * un menu serait une faute — l'utilisateur doit savoir à tout instant dans
 * quel mode il se trouve.
 *
 * Tant qu'aucun broker n'est connecté, CONFIRMATION et AUTO sont désactivés :
 * ils n'auraient rien à quoi envoyer un ordre.
 */
export function ModeSelector({
  mode,
  onChange,
  brokerAvailable,
}: {
  mode: ExecutionMode
  onChange: (mode: ExecutionMode) => void
  brokerAvailable: boolean
}) {
  // Volontairement courtes : ces phrases tiennent sur une ligne, y compris sur
  // un écran de 375 px, où chaque ligne gagnée revient au graphique.
  const descriptions: Record<ExecutionMode, string> = {
    calculator: "Aucun ordre envoyé — vous passez l'ordre vous-même.",
    confirmation: 'Récapitulatif, puis envoi après validation.',
    auto: 'Envoi immédiat de l’ordre inversé à la validation.',
  }

  return (
    // Une seule ligne, pas une carte : ce réglage change une fois par session
    // au plus, et sa version « carte » mangeait 110 px de hauteur en haut de
    // l'écran — juste au-dessus du graphique, l'élément qui en manque le plus.
    // Le libellé reste écrit en toutes lettres : le mode décide si un appui
    // envoie un ordre réel, il ne peut pas se deviner à une icône.
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border border-border bg-panel px-2 py-1.5">
      <span className="shrink-0 pl-1 text-[11px] uppercase tracking-wide text-ink-muted">
        Mode
      </span>
      <div className="min-w-[260px] flex-1">
        <Segmented<ExecutionMode>
          value={mode}
          onChange={onChange}
          disabledOptions={brokerAvailable ? [] : ['confirmation', 'auto']}
          options={[
            { value: 'calculator', label: 'Calculateur' },
            { value: 'confirmation', label: 'Confirmation' },
            { value: 'auto', label: 'Auto' },
          ]}
        />
      </div>
      <p className="flex-1 basis-full text-[11px] leading-snug text-ink-muted lg:basis-auto">
        {descriptions[mode]}
        {!brokerAvailable && (
          <>
            {' '}
            <span className="text-warn">
              Broker non connecté : seul le mode calculateur est disponible.
            </span>
          </>
        )}
      </p>
    </div>
  )
}
